import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Sparkles, Loader2, Download, RefreshCw, FileText, CalendarDays,
  BookOpen, ClipboardList, Users, MessageCircle, GraduationCap, Printer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AppShell from '@/components/AppShell';
import PrintHeader from '@/components/PrintHeader';
import PrintReadyMarker from '@/components/PrintReadyMarker';
import PdfCoverDialog from '@/components/PdfCoverDialog';
import { useDownloadPdf } from '@/hooks/useDownloadPdf';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_LABELS = {
  7:  'Grade 7 · Civics & Government',
  8:  'Grade 8 · US History',
  9:  'Grade 9 · World History',
  10: 'Grade 10 · Modern World History',
  11: 'Grade 11 · US History',
  12: 'Grade 12 · Government & Contemporary Issues',
};

/**
 * SyllabusPage — grade-level course syllabus, LLM-generated once per grade
 * and cached on the server so subsequent visits load instantly. Cover PDF
 * download is wired to the shared PdfCoverDialog.
 */
export default function SyllabusPage() {
  const { grade } = useParams();
  const gradeLevel = parseInt(grade, 10);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [syllabus, setSyllabus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [teacherName, setTeacherName] = useState('');
  const [schoolYear, setSchoolYear] = useState('');
  const [coverOpen, setCoverOpen] = useState(false);
  const { downloading: downloadingPdf, downloadPdf } = useDownloadPdf();

  useEffect(() => {
    const prefs = user?.preferences || {};
    setTeacherName(prefs.default_teacher_name || user?.name || '');
    const y = new Date().getFullYear();
    setSchoolYear(prefs.default_school_year || `${y}\u2013${y + 1} School Year`);
  }, [user]);

  useEffect(() => {
    if (!gradeLevel || !GRADE_LABELS[gradeLevel]) {
      toast.error('Unknown grade');
      navigate('/dashboard');
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/syllabus/${gradeLevel}`);
        setSyllabus(res.data);
      } catch (err) {
        if (err?.response?.status !== 404) {
          toast.error('Failed to load syllabus');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [gradeLevel, navigate]);

  const generate = async (force = false) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/syllabus/generate`, {
        grade_level: gradeLevel,
        school_year: schoolYear,
        teacher_name: teacherName,
        force,
      });
      setSyllabus(res.data);
      toast.success(force ? 'Syllabus regenerated' : 'Syllabus generated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = () => setCoverOpen(true);
  const handleCoverConfirm = async (cover) => {
    const filename = `syllabus-grade-${gradeLevel}.pdf`;
    await downloadPdf({
      resource: 'syllabus',
      resourceId: String(gradeLevel),
      filename,
      cover: {
        ...cover,
        title: content?.course_title || GRADE_LABELS[gradeLevel],
        grade: String(gradeLevel),
        resource_label: 'Course Syllabus',
        subtitle: schoolYear || content?.school_year,
      },
    });
    setCoverOpen(false);
  };

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent" />
        </div>
      </AppShell>
    );
  }

  const content = syllabus?.content;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:max-w-none print:px-0 print:py-2">
        <PrintReadyMarker ready={!loading} />
        <PrintHeader
          resourceType="Course Syllabus"
          resourceTitle={content?.course_title || GRADE_LABELS[gradeLevel]}
          grade={gradeLevel}
        />

        <div className="flex items-center justify-between flex-wrap gap-3 mb-6 print:hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/dashboard/${gradeLevel}`)}
            className="text-[#5A6577] hover:text-[#14213D] -ml-2"
            data-testid="syllabus-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Grade {gradeLevel}
          </Button>
          {content && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="syllabus-print-btn">
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                data-testid="syllabus-download-pdf-btn"
              >
                {downloadingPdf ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> PDF&hellip;</>
                ) : (
                  <><Download className="h-4 w-4 mr-2" /> Download PDF</>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => generate(true)}
                disabled={generating}
                data-testid="syllabus-regenerate-btn"
                title="Regenerate the syllabus from scratch"
              >
                {generating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Regenerating&hellip;</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" /> Regenerate</>
                )}
              </Button>
            </div>
          )}
        </div>

        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Course Syllabus</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#14213D] tracking-tight mb-2">
            {content?.course_title || GRADE_LABELS[gradeLevel]}
          </h2>
          {content?.school_year && (
            <p className="text-sm text-[#5A6577]">{content.school_year}{content.instructor ? ` · ${content.instructor}` : ''}</p>
          )}
        </div>

        {!content ? (
          <Card className="p-8 sm:p-10 print:hidden">
            <div className="max-w-xl">
              <GraduationCap className="h-9 w-9 text-[#D4AF37] mb-3" strokeWidth={1.5} />
              <h3 className="font-serif-display text-2xl text-[#14213D] tracking-tight mb-2">
                Generate this year&rsquo;s syllabus
              </h3>
              <p className="text-sm text-[#4A5568] mb-6 leading-relaxed">
                Atlas will draft a full-year syllabus for <strong>{GRADE_LABELS[gradeLevel]}</strong> using the real curriculum units already seeded for this grade — course description, learning outcomes, grading breakdown, and a unit-by-unit schedule.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label htmlFor="syllabus-teacher" className="text-xs uppercase tracking-wider text-[#14213D]">Teacher</Label>
                  <Input
                    id="syllabus-teacher"
                    value={teacherName}
                    onChange={(e) => setTeacherName(e.target.value)}
                    placeholder="e.g., Mr. Casteel"
                    className="mt-1"
                    data-testid="syllabus-teacher-input"
                  />
                </div>
                <div>
                  <Label htmlFor="syllabus-year" className="text-xs uppercase tracking-wider text-[#14213D]">School Year</Label>
                  <Input
                    id="syllabus-year"
                    value={schoolYear}
                    onChange={(e) => setSchoolYear(e.target.value)}
                    placeholder="e.g., 2025-2026 School Year"
                    className="mt-1"
                    data-testid="syllabus-year-input"
                  />
                </div>
              </div>
              <Button
                onClick={() => generate(false)}
                disabled={generating}
                className="bg-[#14213D] text-[#FDFBF7] hover:bg-[#0f172a]"
                data-testid="syllabus-generate-btn"
              >
                {generating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating&hellip;</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2 text-[#D4AF37]" /> Generate Syllabus</>
                )}
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-6" data-print-block data-testid="syllabus-content">
            <Card className="p-6 sm:p-7">
              <h3 className="font-serif text-lg text-[#14213D] mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#D4AF37]" /> Course description
              </h3>
              <p className="text-sm text-[#2D3748] leading-relaxed">{content.course_description}</p>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-6">
                <h3 className="font-serif text-lg text-[#14213D] mb-3 flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-[#D4AF37]" /> Learning outcomes
                </h3>
                <ul className="space-y-2 text-sm text-[#2D3748]">
                  {(content.learning_outcomes || []).map((o) => (
                    <li key={o.slice(0, 40)} className="flex gap-2">
                      <span className="text-[#D4AF37] mt-0.5">•</span>
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-6">
                <h3 className="font-serif text-lg text-[#14213D] mb-3 flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-[#D4AF37]" /> Materials required
                </h3>
                <ul className="space-y-2 text-sm text-[#2D3748]">
                  {(content.materials_required || []).map((m) => (
                    <li key={m.slice(0, 40)} className="flex gap-2">
                      <span className="text-[#D4AF37] mt-0.5">•</span>
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>

            <Card className="p-6 sm:p-7">
              <h3 className="font-serif text-lg text-[#14213D] mb-3 flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-[#D4AF37]" /> Unit schedule
              </h3>
              <div className="overflow-hidden rounded-md border border-[#EBE7E0]">
                <table className="w-full text-sm">
                  <thead className="bg-[#F9F8F4] text-[#14213D]">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold w-16">#</th>
                      <th className="text-left px-3 py-2 font-semibold">Unit</th>
                      <th className="text-left px-3 py-2 font-semibold w-32">Weeks</th>
                      <th className="text-left px-3 py-2 font-semibold hidden sm:table-cell">Focus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(content.unit_schedule || []).map((u) => (
                      <tr key={`u-${u.unit_number}`} className="border-t border-[#EBE7E0]">
                        <td className="px-3 py-2 font-mono tabular-nums text-[#5A6577]">{u.unit_number}</td>
                        <td className="px-3 py-2 font-medium text-[#14213D]">{u.title}</td>
                        <td className="px-3 py-2 text-[#5A6577]">{u.approximate_weeks ? `${u.approximate_weeks} wk` : '—'}</td>
                        <td className="px-3 py-2 text-[#2D3748] hidden sm:table-cell">{u.focus}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card className="p-6 sm:p-7">
              <h3 className="font-serif text-lg text-[#14213D] mb-3 flex items-center gap-2">
                <GraduationCap className="h-4 w-4 text-[#D4AF37]" /> Grading policy
              </h3>
              <p className="text-sm text-[#2D3748] mb-4">{content.grading_policy?.assessments}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(content.grading_policy?.breakdown || []).map((b) => (
                  <div key={b.category} className="p-3 bg-[#F9F8F4] rounded-md border border-[#EBE7E0]">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-[#5A6577] font-semibold">{b.category}</p>
                    <p className="font-serif-display text-2xl text-[#14213D] mt-1">{b.weight_percent}%</p>
                  </div>
                ))}
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-6">
                <h3 className="font-serif text-lg text-[#14213D] mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4 text-[#D4AF37]" /> Classroom expectations
                </h3>
                <ul className="space-y-2 text-sm text-[#2D3748]">
                  {(content.classroom_expectations || []).map((e) => (
                    <li key={e.slice(0, 40)} className="flex gap-2">
                      <span className="text-[#D4AF37] mt-0.5">•</span>
                      <span>{e}</span>
                    </li>
                  ))}
                </ul>
              </Card>
              <Card className="p-6">
                <h3 className="font-serif text-lg text-[#14213D] mb-3 flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-[#D4AF37]" /> Communication
                </h3>
                <p className="text-sm text-[#2D3748] leading-relaxed mb-3">{content.communication_policy}</p>
                <h4 className="text-xs uppercase tracking-wider text-[#5A6577] font-semibold mt-4">Assessment plan</h4>
                <p className="text-sm text-[#2D3748] leading-relaxed mt-1">{content.assessment_plan}</p>
              </Card>
            </div>
          </div>
        )}
      </div>
      <PdfCoverDialog
        open={coverOpen}
        onOpenChange={setCoverOpen}
        onConfirm={handleCoverConfirm}
        downloading={downloadingPdf}
        context={{
          title: content?.course_title || GRADE_LABELS[gradeLevel],
          resource_label: 'Course Syllabus',
          grade: gradeLevel,
        }}
      />
    </AppShell>
  );
}
